import React, { useContext, useState } from 'react';
import { Alert, Box, Button, FormField, Input, Modal, Select, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { MultidimensionalAllocationContext } from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import { CommonBalancePoolGrain } from 'src/models/multidimensional-allocation/CommonBalancePool';
import ErrorUtils from 'src/utils/ErrorUtils';

interface CreateCommonBalancePoolModalProps {
    visible: boolean;
    onHide: () => void;
    /** Called after a successful create so the list can refresh. */
    onCreated: () => void;
}

const GRAIN_OPTIONS: SelectProps.Option[] = Object.values(CommonBalancePoolGrain).map((grain) => ({
    label: grain,
    value: grain,
}));

/**
 * Create-pool dialog — name + grain (+ optional description), mirroring the Manage Allocation Sheets
 * create modal. Calls the real CreateCommonBalancePool WebAPI through CommonBalancePoolService (no
 * mock); the pool is created as a draft. Column configuration is a later CR.
 */
export default function CreateCommonBalancePoolModal({ visible, onHide, onCreated }: CreateCommonBalancePoolModalProps) {
    const { services } = useContext(MultidimensionalAllocationContext);
    const service = services.commonBalancePoolService;

    const [poolName, setPoolName] = useState('');
    const [description, setDescription] = useState('');
    const [grain, setGrain] = useState<SelectProps.Option | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [nameError, setNameError] = useState<string | undefined>(undefined);
    const [grainError, setGrainError] = useState<string | undefined>(undefined);

    const reset = () => {
        setPoolName('');
        setDescription('');
        setGrain(null);
        setError(undefined);
        setNameError(undefined);
        setGrainError(undefined);
    };

    const handleCancel = () => {
        // Ignore cancel/dismiss while a create is in flight: otherwise the modal closes but the
        // in-flight createCommonBalancePool still resolves and fires reset()/onCreated()/onHide(),
        // refreshing the list with a pool the user thought they cancelled. Guards Cancel, the X and Escape.
        if (submitting) return;
        reset();
        onHide();
    };

    const handleCreate = async () => {
        setError(undefined);
        const nErr = poolName.trim() ? undefined : 'Name is required';
        const gErr = grain ? undefined : 'Grain is required';
        setNameError(nErr);
        setGrainError(gErr);
        if (nErr || gErr) {
            return;
        }

        try {
            setSubmitting(true);
            await service.createCommonBalancePool({
                poolName: poolName.trim(),
                grain: grain!.value as CommonBalancePoolGrain,
                description: description.trim() || undefined,
            });
            reset();
            onCreated();
            onHide();
        } catch (ex) {
            // ErrorUtils.getMessage normalizes business-API error bodies + typed errors (e.g. EntityLockedError),
            // matching CommonBalancePoolService and the rest of the MDA components.
            setError(ErrorUtils.getMessage(ex));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            visible={visible}
            onDismiss={(event) => {
                // Overlay clicks intentionally don't dismiss: creating a pool is a deliberate action,
                // so we only close via the X or Cancel to avoid discarding entered form state on a stray click.
                if (event.detail.reason !== 'overlay') {
                    handleCancel();
                }
            }}
            header="Create common balance pool"
            closeAriaLabel="Close modal"
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleCancel} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleCreate} loading={submitting} disabled={submitting}>
                            Create
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <Box color="text-body-secondary">
                    Name the pool and choose its grain. It is created as a draft.
                </Box>

                <FormField label="Name" errorText={nameError}>
                    <Input
                        value={poolName}
                        onChange={({ detail }) => {
                            setPoolName(detail.value);
                            setNameError(undefined); // clear the validation error as the user fixes it
                        }}
                        placeholder="Enter a pool name"
                    />
                </FormField>

                <FormField
                    label="Grain"
                    errorText={grainError}
                    description="Fixed at creation — every input and materialised row of the pool shares this grain."
                >
                    <Select
                        selectedOption={grain}
                        onChange={({ detail }) => {
                            setGrain(detail.selectedOption);
                            setGrainError(undefined); // clear the validation error once a grain is chosen
                        }}
                        options={GRAIN_OPTIONS}
                        placeholder="Select a grain"
                    />
                </FormField>

                <FormField label="Description - optional">
                    <Textarea
                        value={description}
                        onChange={({ detail }) => setDescription(detail.value)}
                        placeholder="Enter a description"
                        rows={3}
                    />
                </FormField>

                {!!error && (
                    <Alert type="error" dismissible header="Create failed" onDismiss={() => setError(undefined)}>
                        {error}
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    );
}
